'use client';

/** @jsxImportSource react */

import { useEffect } from 'react';

import { reportClientError } from './rollbar-client';

/**
 * v2 App Router route-segment error boundary. Server-thrown errors are already
 * captured with a full stack by instrumentation.ts's onRequestError; this
 * boundary reports the client-visible failure too (carrying `digest`, which
 * correlates the two items in Rollbar) and catches genuinely client-side render
 * errors that never touch the server.
 */
export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    reportClientError(error, { boundary: 'app/error', digest: error.digest });
  }, [error]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-8 text-center">
      <h1 className="text-xl font-semibold text-neutral-900">
        Something went wrong
      </h1>
      <p className="max-w-md text-sm text-neutral-600">
        We hit an unexpected error and our team has been notified. Please try
        again.
      </p>
      <button
        className="rounded-lg bg-neutral-900 px-4 py-2 text-sm font-medium text-white"
        type="button"
        onClick={reset}
      >
        Try again
      </button>
    </div>
  );
}
